/* CommitPushAction.js */

"use strict";

const _ = require("lodash");

const CommitMessageVeilComponent = require("./CommitMessageVeilComponent");
const Steve = require("@gluenetworks/steve_core").getInstance();

const CONFIG_NAME_MAX_LENGTH = 60;
const CONFIG_NAME_LIST_LENGTH = 10;

class CommitPushAction {
    constructor(logger, gameEditor, modalUserFeedback, activeView, userSession, gitService) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = modalUserFeedback;
        this._activeView = activeView;
        this._activeOrgId = userSession.activeOrganization.id;
        this._gitService = gitService;
        this._selectedIds = [];
        if (this._activeView && this._activeView.pagingFeed) {
            this._selectedIds = this._activeView.getSelectedIds().selectedRows || [];
        }

        this.run = this.run.bind(this);
    }

    run() {
        const selectedIds = this._selectedIds || [];
        if (!selectedIds.length) {
            return;
        }

        let selectedNames = [];

        // Retrieve names if fewer than CONFIG_NAME_LIST_LENGTH
        if (selectedIds.length <= CONFIG_NAME_LIST_LENGTH) {
            selectedNames = this._activeView.pagingFeed.getEntitiesNameById(selectedIds);
            if (!Array.isArray(selectedNames) || selectedNames.length === 0) {
                this._logger.error("Failed to retrieve selected names");
            }
        }

        // Build confirmation message
        const pluralSuffix = selectedIds.length > 1 ? "s" : "";
        const configCount = pluralSuffix && selectedIds.length ? selectedIds.length : "";
        let message = `Are you sure you want to commit and push the ${configCount} selected configuration${pluralSuffix}?\n`;

        selectedNames.forEach((name, index) => {
            const entityId = selectedIds[index];
            const cacheEntry = _.get(this._activeView, ["pagingFeed", "cache", entityId], {});
            const displayType = (cacheEntry.displayType || "unknown").toLowerCase();
            const shortName =
                name.length > CONFIG_NAME_MAX_LENGTH
                    ? name.substring(0, CONFIG_NAME_MAX_LENGTH) + "…"
                    : name;

            message += `\n• ${shortName} (${displayType})`;
        });

        message += "\n\nPlease specify a commit message:";

        // Display confirmation dialog
        return this._modalUserFeedback
            .displayCustom(new CommitMessageVeilComponent(this._logger, message), {dontHide: false})
            .then((commitMessage) => {
                const listOfFiles = selectedIds.map((entityId) => {
                    const cacheEntry = _.get(
                        this._activeView,
                        ["pagingFeed", "cache", entityId],
                        {}
                    );
                    const category = cacheEntry.category;
                    const type = cacheEntry.type;
                    const typeName = cacheEntry.typeName ? cacheEntry.typeName : undefined;
                    const featureType = cacheEntry.featureType;
                    const featureInstance = cacheEntry.featureInstance;

                    if (!type) {
                        throw new Error(`Missing 'type' for entity/construct ID: ${entityId}`);
                    }

                    if (category === "construct" && !typeName) {
                        throw new Error(`Missing 'typeName' for entity/construct ID: ${entityId}`);
                    }

                    return {
                        id: entityId.split("#")[0],
                        type,
                        typeName,
                        featureType,
                        featureInstance,
                    };
                });

                return this._modalUserFeedback
                    .displayLoading({
                        message: "Committing and Pushing... This could take a moment",
                        notDismissable: false,
                    })
                    .then(() => {
                        return this._gitService
                            .commitAndPush({
                                message: commitMessage,
                                orgId: this._activeOrgId,
                                listOfFiles,
                            })
                            .then(() => {
                                return this._modalUserFeedback.hideDisplayed();
                            })
                            .then(() => {
                                this._activeView.resetSelection();
                                this._activeView.pagingFeed.reestablish();
                                const confirmationMessage =
                                    selectedNames.length > 1
                                        ? "The selected configurations were successfully committed and pushed."
                                        : "The selected configuration was successfully committed and pushed.";
                                return this._modalUserFeedback.displayNotification(
                                    confirmationMessage,
                                    {
                                        title: "Commit and Push",
                                    }
                                );
                            });
                    });
            })
            .catch((err) => {
                return this._modalUserFeedback.hideDisplayed().then(() => {
                    if (!err) {
                        err = new Error("An unknown error was encountered.");
                    }
                    if (err.userRejected) {
                        this._logger.warn({error: err}, "CommitPush action - User Canceled");
                        this._modalUserFeedback.hideDisplayed();
                    } else {
                        this._logger.warn({error: err}, "CommitPush action - Error");
                        return this._modalUserFeedback.displayError(err);
                    }
                });
            });
    }
}

module.exports = CommitPushAction;

CommitPushAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "gameEditor.activeView",
    "$teve.userSession",
    "$teve.services.Git",
];

CommitPushAction.$displayDetails = {
    type: "Action",
    name: "CommitPushAction",
    icon: "versioncontrol.commit",
    shown: true,
    title: "Commit and Push",
    order: 1,
};
